// src/Community/RecipeCard.js
import React, {useEffect, useState, useRef, useMemo, useCallback} from 'react';
import {Link, useNavigate} from 'react-router-dom';
import {
  doc, getDoc, collection, query, where,
  getDocs, updateDoc, deleteDoc
} from 'firebase/firestore';
import {db} from '../config/firebase';
import {handleAddComment as addCommentWithNotification} from '../utils/dbUtils';
import CommentsSection from './CommentsSection';
import AddToRecetarioModal from '../RecipeDB/AddToRecetarioModal';
import likeIcon from '../Icons/like.png';
import likedIcon from '../Icons/liked1.png';
import saveIcon from '../Icons/save.png';
import savedIcon from '../Icons/saved1.png';
import commentIcon from '../Icons/comment.png';
import getRecipeId from '../utils/getRecipeId';
import getRecipeLikesCount from '../RecipeDB/getRecipeLikesCount';
import { useToast } from '../context/ToastContext';
import './RecipeCard.css';
import ImageSwiper from './ImageSwiper';
import { openRecipeInChat } from '../utils/openRecipeInChat';
import COOKY_ICON from '../Icons/CookyChat_Oficial_cir.png';
import { usePlan } from '../context/PlanContext';

const RecipeCard = ({recipe, userId, userNames, handleLike, handleAddToFavorites, handleRemoveFromFavorites, favoriteRecipesIds}) => {
  const [showMenu, setShowMenu] = useState(false);
  const [showShare, setShowShare] = useState(false);
  const [showRecetarioModal, setShowRecetarioModal] = useState(false);
  const [privacidad, setPrivacidad] = useState(recipe.privacidad || 'seguidores');
  const [likesCount, setLikesCount] = useState(0);
  const [hasLiked, setHasLiked] = useState(false);
  const [showAllComments, setShowAllComments] = useState(false);
  const [comments, setComments] = useState([]);
  const [newComment, setNewComment] = useState('');
  const menuRef = useRef(null);
  const navigate = useNavigate();
  const { showToast } = useToast();
  const { isPremium, showUpgradeModal } = usePlan();

  // Normalize owner detection
  const isOwner = useMemo(() => {
    const recipeOwnerId =
      recipe?.userId ??
      recipe?.user?.id ??
      recipe?.ownerId ??
      recipe?.creatorId ??
      null;
    return !!(
      userId &&
      recipeOwnerId &&
      String(userId) === String(recipeOwnerId)
    );
  }, [userId, recipe]);

  const canOpenInChat = true; // visible en todas las recetas que el usuario puede ver

  // RESTAURAR fetchComments
  const fetchComments = async (recipeId) => {
    const commentsQuery = query(
      collection(db, 'comments'),
      where('recipeId', '==', recipeId)
    );
    const commentsSnapshot = await getDocs(commentsQuery);
    const commentsList = commentsSnapshot.docs.map(d => ({id: d.id, ...d.data()}));
    setComments(commentsList);
  };

  // handleAddComment con notificación al dueño de la receta
  const handleAddComment = async (recipeId) => {
    if (!newComment.trim()) return;
    await addCommentWithNotification(recipeId, userId, newComment, setNewComment, fetchComments);
  };

  // Editar comentario
  const handleEditComment = async (recipeId, commentId, newText) => {
    await updateDoc(doc(db, 'comments', commentId), { text: newText });
    fetchComments(recipeId);
  };

  // Eliminar comentario
  const handleDeleteComment = async (recipeId, commentId) => {
    await deleteDoc(doc(db, 'comments', commentId));
    fetchComments(recipeId);
    showToast('Comentario eliminado', 'info');
  };

  const fetchLikes = useCallback(async () => { // <- useCallback para estabilizar
    const count = await getRecipeLikesCount(getRecipeId(recipe));
    setLikesCount(count);
  }, [recipe]); // <- recipe ya está en los deps del useEffect

  useEffect(() => {
    const id = getRecipeId(recipe);
    if (id && userId) {
      fetchComments(id);
      fetchLikes();
      const checkLike = async () => {
        const likeRef = doc(db, 'recetas', id, 'likes', userId);
        const likeSnap = await getDoc(likeRef);
        setHasLiked(likeSnap.exists());
      };
      checkLike();
    }
  }, [recipe, userId, fetchLikes]); // <- añadir fetchLikes (estable via useCallback)

  const toggleComments = () => setShowAllComments(v => !v);

  const isFavorite = favoriteRecipesIds.includes(getRecipeId(recipe));

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (menuRef.current && !menuRef.current.contains(event.target)) {
        setShowMenu(false);
        setShowShare(false); // <- fix: también resetea el submenú de compartir
      }
    };
    if (showMenu) document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [showMenu]);

  const handleDeleteRecipe = async () => {
        try {
            // Elimina la receta del array del usuario loggeado
            const userDocRef = doc(db, 'users', userId);
            const userDoc = await getDoc(userDocRef);
            if (!userDoc.exists()) throw new Error('Usuario no encontrado');
            const recetasCompartidas = userDoc.data().recetasCompartidas || [];
            // Filtra tanto si son objetos {id: ...} como si son strings
            const nuevasRecetas = recetasCompartidas.filter(r => {
                if (typeof r === 'string') return r !== recipe.id;
                if (typeof r === 'object' && r.id) return r.id !== recipe.id;
                return true;
         });
            await updateDoc(userDocRef, {recetasCompartidas: nuevasRecetas});

            // Elimina el documento de la receta en la colección recetas
            await deleteDoc(doc(db, 'recetas', recipe.id));

            setShowMenu(false);
     } catch (error) {
           showToast('Error al eliminar la receta', 'error');
     }
 };

  const handleChangePrivacidad = async () => {
    const ciclo = { publica: 'seguidores', seguidores: 'privada', privada: 'publica' };
    const nuevoValor = ciclo[privacidad] || 'publica';
    setPrivacidad(nuevoValor);
    try {
        await updateDoc(doc(db, 'recetas', recipe.id), { privacidad: nuevoValor });
    } catch (error) {
        showToast('Error al actualizar la privacidad', 'error');
    }
    setShowMenu(false);
 };

  const buildRecipeUrl = () => {
    const id = getRecipeId(recipe);
    return `${window.location.origin}/recipe/${id}`;
  };

  const handleCopyLink = async () => {
    try {
      await navigator.clipboard.writeText(`https://cookychat.com/recipe/${getRecipeId(recipe)}`);
      showToast('¡Link copiado al portapapeles!', 'success');
    } catch {
      showToast('No se pudo copiar el link', 'error');
    }
    setShowMenu(false);
  };

  const handleShareWhatsApp = () => {
    const url = buildRecipeUrl();
    const text = encodeURIComponent(`¡Mira esta receta de Cookychat!\n${url}`);
    const wa = `https://wa.me/?text=${text}`;
    window.open(wa, '_blank');
    setShowMenu(false);
    setShowShare(false);
  };

  const handleShareGmail = () => {
    const url = buildRecipeUrl();
    const subject = encodeURIComponent('Receta de Cookychat');
    const body = encodeURIComponent(`¡Mira esta receta de Cookychat!\n${url}`);
    const mailto = `mailto:?subject=${subject}&body=${body}`;
    window.open(mailto, '_blank');
    setShowMenu(false);
    setShowShare(false);
  };

  // Optimistic like: actualiza la UI antes de confirmar Firestore
  const handleOptimisticLike = async () => {
    const recipeId = getRecipeId(recipe);
    const wasLiked = hasLiked;
    const prevCount = likesCount;

    // Actualizar UI inmediatamente
    setHasLiked(!wasLiked);
    setLikesCount(c => wasLiked ? c - 1 : c + 1);

    try {
        await handleLike(recipeId);
    } catch {
        // Revertir si falla
        setHasLiked(wasLiked);
        setLikesCount(prevCount);
        showToast('Error al procesar el like', 'error');
    }
};

  return (
    <div className="recipe-card">
      <div className="recipe-header">
        <Link to={`/profile/${recipe.userId}`} className="recipe-user">
          {userNames[recipe.userId] || recipe.userName}
        </Link>
        <div className="recipe-menu-wrapper">
          <span className="recipe-menu-icon" onClick={() => setShowMenu(v => !v)}>⋮</span>

          {showMenu && (
            <div ref={menuRef} className="recipe-menu-dropdown">
              {isOwner ? (
                <div>
                  <button className={`recipe-menu-btn ${privacidad === 'privada' ? 'private' : privacidad === 'seguidores' ? 'followers' : 'public'}`} onClick={handleChangePrivacidad}>
                    {privacidad === 'privada' ? '🔒 Sólo para mí' : privacidad === 'seguidores' ? '👥 Solo seguidores' : '🌍 Pública'}
                  </button>
                  <div className="menu-separator" />

                  {/* Compartir justo después de privacidad */}
                  <button
                    className="recipe-menu-btn"
                    onClick={() => setShowShare(s => !s)}
                  >
                    📤 Compartir...
                  </button>

                  {showShare && (
                    <div className="share-dropdown" onClick={e => e.stopPropagation()}>
                      <button className="recipe-menu-btn" onClick={handleCopyLink}>🔗 Copiar Link</button>
                      <button className="recipe-menu-btn" onClick={handleShareWhatsApp}>💬 Whatsapp</button>
                      <button className="recipe-menu-btn" onClick={handleShareGmail}>✉️ GMAIL</button>
                    </div>
                  )}

                  <div className="menu-separator" />
                  <button className="recipe-menu-btn" onClick={() => { setShowRecetarioModal(true); setShowMenu(false); }}>📚 Añadir a recetario</button>
                  <div className="menu-separator" />
                  <button className="recipe-menu-btn edit" onClick={() => navigate(`/edit-recipe/${recipe.id}`)}>✏️ Editar receta</button>
                  <button className="recipe-menu-btn delete" onClick={handleDeleteRecipe}>🗑️ Eliminar receta</button>
                </div>
              ) : (
                <div>
                  {/* Compartir para todos */}
                  <button
                    className="recipe-menu-btn"
                    onClick={() => setShowShare(s => !s)}
                  >
                    📤 Compartir...
                  </button>

                  {showShare && (
                    <div className="share-dropdown" onClick={e => e.stopPropagation()}>
                      <button className="recipe-menu-btn" onClick={handleCopyLink}>🔗 Copiar Link</button>
                      <button className="recipe-menu-btn" onClick={handleShareWhatsApp}>💬 Whatsapp</button>
                      <button className="recipe-menu-btn" onClick={handleShareGmail}>✉️ GMAIL</button>
                    </div>
                  )}
                  <div className="menu-separator" />
                  <button className="recipe-menu-btn" onClick={() => { setShowRecetarioModal(true); setShowMenu(false); }}>📚 Añadir a recetario</button>
                </div>
              )}
            </div>
          )}
        </div>
      </div>
      <h3
        className="recipe-title"
        onClick={() => navigate(`/recipe/${getRecipeId(recipe)}`)}
      >
        {recipe.nombre}
      </h3>
      {/* REEMPLAZAR el bloque recipe-image-wrapper + img por ImageSwiper */}
      <div
        className="recipe-image-wrapper"
        onClick={() => navigate(`/recipe/${getRecipeId(recipe)}`)}
      >
        <ImageSwiper
          imagenes={recipe.imagenes}
          imagen={recipe.imagen}
          nombre={recipe.nombre}
        />
        {recipe.videoUrl && (
          <div className="recipe-video-badge">▶</div>
        )}
      </div>
      <div className="recipe-actions">
        <span className="recipe-likes">{likesCount} Me gusta</span>
        <img src={hasLiked ? likedIcon : likeIcon} alt="Like"
          onClick={handleOptimisticLike}
          className={`icon${hasLiked ? ' icon-liked' : ''}`}
        />
        <img src={isFavorite ? savedIcon : saveIcon}
          alt={isFavorite ? "Guardada" : "Guardar"}
          className="icon"
          onClick={() => isFavorite ? handleRemoveFromFavorites(recipe) : handleAddToFavorites(recipe)}
        />
        <img src={commentIcon} alt="Comentar"
          onClick={() => navigate(`/recipe/${getRecipeId(recipe)}`)}
          className="icon"
        />
        {canOpenInChat && (
          <img
            src={COOKY_ICON}
            alt="Chef IA"
            className={`icon icon-cooky-ai${!isPremium ? ' icon-cooky-locked' : ''}`}
            onClick={() => {
              if (!isPremium) {
                showUpgradeModal('aiRecipeChat');
                return;
              }
              openRecipeInChat(recipe, userId, navigate);
            }}
            title={isPremium ? 'Hablar con IA sobre esta receta' : 'Disponible con Premium'}
          />
        )}
      </div>
      <CommentsSection
        recipeId={getRecipeId(recipe)}
        comments={{[getRecipeId(recipe)]: comments}}
        newComment={newComment}
        setNewComment={setNewComment}
        handleAddComment={handleAddComment}
        handleEditComment={handleEditComment}
        handleDeleteComment={handleDeleteComment}
        userNames={userNames}
        currentUserId={userId}
        showAllComments={showAllComments}
        toggleComments={toggleComments}
      />

      {showRecetarioModal && (
        <AddToRecetarioModal
          recipe={recipe}
          onClose={() => setShowRecetarioModal(false)}
        />
      )}
    </div>
  );
};

export default RecipeCard;